import React, { useState, useRef, useEffect, useCallback } from "react";
import { formatPricePerSquareMeter } from "./JPBFunc";
import { TbRotateClockwise } from "react-icons/tb";
import { Localization } from "@viamap/viamap2-common";
import { AreaUnit } from "./JPBState";

type PriceRangeSliderProps = {
   min: number;
   max: number;
   currentMin: number;
   currentMax: number;
   onRangeChange: (min: number, max: number) => void;
   onResetToSystem?: () => void;
   areaUnit: AreaUnit;
};

export function PriceRangeSlider(props: PriceRangeSliderProps) {
   const [isDragging, setIsDragging] = useState<'min' | 'max' | 'range' | null>(null);
   const [localMin, setLocalMin] = useState(Math.round(props.currentMin));
   const [localMax, setLocalMax] = useState(Math.round(props.currentMax));
   const sliderRef = useRef<HTMLDivElement>(null);
   const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
   const rangeDragStartRef = useRef<{ initialMin: number; initialMax: number; initialValue: number } | null>(null);

   useEffect(() => {
      setLocalMin(Math.round(props.currentMin));
      setLocalMax(Math.round(props.currentMax));
   }, [props.currentMin, props.currentMax]);

   const logScale = useCallback((value: number, min: number, max: number): number => {
      const safeMin = Math.max(1, min);
      const safeMax = Math.max(1, max);
      const safeValue = Math.max(1, value);

      if (safeMin >= safeMax) {
         return 0;
      }

      const logMin = Math.log(safeMin);
      const logMax = Math.log(safeMax);
      const logValue = Math.log(safeValue);
      const logRange = logMax - logMin;

      if (logRange === 0) {
         return 0;
      }

      const result = (logValue - logMin) / logRange;
      return Math.max(0, Math.min(1, result));
   }, []);

   const fromLogScale = useCallback((percentage: number, min: number, max: number): number => {
      const safeMin = Math.max(1, min);
      const safeMax = Math.max(1, max);

      if (percentage >= 1) {
         return max + 1;
      }

      if (percentage <= 0) {
         return 1;
      }

      const logMin = Math.log(safeMin);
      const logMax = Math.log(safeMax);
      const logValue = logMin + percentage * (logMax - logMin);

      return Math.max(1, Math.exp(logValue));
   }, []);

   const minPosition = Math.max(0, Math.min(100, logScale(localMin, props.min, props.max) * 100));
   const maxPosition = Math.max(0, Math.min(100, logScale(localMax, props.min, props.max) * 100));

   const adjustedMinPosition = minPosition;
   const adjustedMaxPosition = Math.max(minPosition, maxPosition);

   const debouncedOnChange = useCallback((newMin: number, newMax: number) => {
      if (debounceTimeoutRef.current) {
         clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
         props.onRangeChange(newMin, newMax);
      }, 200);
   }, [props]);

   const getValueFromPosition = useCallback((clientX: number): number => {
      if (!sliderRef.current) return props.min;
      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const value = fromLogScale(percentage, props.min, props.max);
      return Math.round(value);
   }, [props.min, props.max, fromLogScale]);

   const handleMove = useCallback((clientX: number) => {
      if (!isDragging) return;

      const value = getValueFromPosition(clientX);

      if (isDragging === 'min') {
         const newMin = Math.max(Math.floor(props.min), Math.min(value, Math.floor(localMax)));
         setLocalMin(newMin);
         debouncedOnChange(newMin, localMax);
      } else if (isDragging === 'max') {
         const newMax = Math.min(Math.ceil(props.max), Math.max(value, Math.ceil(localMin)));
         setLocalMax(newMax);
         debouncedOnChange(localMin, newMax);
      } else if (isDragging === 'range' && rangeDragStartRef.current) {
         const valueOffset = value - Math.round(rangeDragStartRef.current.initialValue);
         const rangeWidth = Math.round(rangeDragStartRef.current.initialMax - rangeDragStartRef.current.initialMin);

         let newMin = Math.round(rangeDragStartRef.current.initialMin) + valueOffset;
         let newMax = Math.round(rangeDragStartRef.current.initialMax) + valueOffset;

         if (newMin < Math.floor(props.min)) {
            newMin = Math.floor(props.min);
            newMax = Math.min(Math.ceil(props.max), newMin + rangeWidth);
         } else if (newMax > Math.ceil(props.max)) {
            newMax = Math.ceil(props.max);
            newMin = Math.max(Math.floor(props.min), newMax - rangeWidth);
         }

         setLocalMin(newMin);
         setLocalMax(newMax);
         debouncedOnChange(newMin, newMax);
      }
   }, [isDragging, localMin, localMax, props.min, props.max, getValueFromPosition, debouncedOnChange]);

   const handleMouseDown = (e: React.MouseEvent, handle: 'min' | 'max' | 'range') => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(handle);

      if (handle === 'range' && sliderRef.current) {
         const rect = sliderRef.current.getBoundingClientRect();
         const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
         const initialValue = fromLogScale(percentage, props.min, props.max);
         rangeDragStartRef.current = {
            initialMin: Math.round(localMin),
            initialMax: Math.round(localMax),
            initialValue: Math.round(initialValue)
         };
      }
   };

   const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX);
   };

   const handleMouseUp = () => {
      setIsDragging(null);
      rangeDragStartRef.current = null;
   };

   const handleTouchStart = (e: React.TouchEvent, handle: 'min' | 'max' | 'range') => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(handle);

      if (handle === 'range' && sliderRef.current && e.touches.length > 0) {
         const rect = sliderRef.current.getBoundingClientRect();
         const percentage = Math.max(0, Math.min(1, (e.touches[0].clientX - rect.left) / rect.width));
         const initialValue = fromLogScale(percentage, props.min, props.max);
         rangeDragStartRef.current = {
            initialMin: Math.round(localMin),
            initialMax: Math.round(localMax),
            initialValue: Math.round(initialValue)
         };
      }
   };

   const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
         handleMove(e.touches[0].clientX);
      }
   };

   const handleTouchEnd = () => {
      setIsDragging(null);
      rangeDragStartRef.current = null;
   };

   useEffect(() => {
      if (isDragging) {
         document.addEventListener('mousemove', handleMouseMove);
         document.addEventListener('mouseup', handleMouseUp);
         document.addEventListener('touchmove', handleTouchMove);
         document.addEventListener('touchend', handleTouchEnd);
         return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
         };
      }
   }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

   useEffect(() => {
      return () => {
         if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
         }
      };
   }, []);

   return (
      <div className="PriceRangeSlider">
         {props.onResetToSystem && (
            <button
               onClick={props.onResetToSystem}
               title={Localization.getText("Reset to system assumptions")}
               className="PriceRangeSlider-ResetButton"

               onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.borderColor = '#3a8e87';
               }}
               onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#d1d5db';
               }}
            >
               <TbRotateClockwise size={18} />
            </button>
         )}
         <div
            ref={sliderRef}
            className="PriceRangeSlider-Slider"
         >
            <div className="PriceRangeSlider-Slider-Background" />

            <div
               className="PriceRangeSlider-Slider-Mask"
               style={{
                  left: `${adjustedMinPosition}%`,
                  width: `${Math.max(0, adjustedMaxPosition - adjustedMinPosition)}%`,
               }}
               onMouseDown={(e) => handleMouseDown(e, 'range')}
               onTouchStart={(e) => handleTouchStart(e, 'range')}
            />

            <div
               data-handle="min"
               className="PriceRangeSlider-Slider-Handle"
               style={{
                  left: `${adjustedMinPosition}%`,
               }}
               onMouseDown={(e) => handleMouseDown(e, 'min')}
               onTouchStart={(e) => handleTouchStart(e, 'min')}
            >
               <div className="PriceRangeSlider-Slider-Handle-Label">
                  {formatPricePerSquareMeter(localMin, props.areaUnit, 0)}
               </div>
            </div>

            <div
               data-handle="max"
               className="PriceRangeSlider-Slider-Handle"
               style={{
                  left: `${adjustedMaxPosition}%`,
               }}
               onMouseDown={(e) => handleMouseDown(e, 'max')}
               onTouchStart={(e) => handleTouchStart(e, 'max')}
            >
               <div className="PriceRangeSlider-Slider-Handle-Label">
                  {formatPricePerSquareMeter(localMax, props.areaUnit, 0)}
               </div>
            </div>
         </div>
      </div>
   );
}

